import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@artstorefronts/ui';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { FLAGGED_IMAGES_QUERY } from '../../graphql/queries';
import { RESOLVE_SCAN_MUTATION } from '../../graphql/mutations';
import { getModerationContentUrl, getModerationFlagReason } from '../../utils/moderation-content';

interface SocialImagePreview {
  id: string;
  thumbnailUrl?: string | null;
  optimizedUrl?: string | null;
}

interface FlaggedScan {
  id: string;
  contentType: string;
  contentId: string;
  scanType: string;
  isFlagged: boolean;
  scannedAt: string;
  result?: Record<string, any> | null;
  image?: {
    id: string;
    title?: string | null;
    thumbnailUrl?: string | null;
    optimizedUrl?: string | null;
  } | null;
  socialImage?: SocialImagePreview | null;
  communityPost?: {
    id: string;
    slug: string;
    title: string;
    type: string;
    content?: string | null;
    socialImages?: SocialImagePreview[] | null;
    textImages?: SocialImagePreview[] | null;
  } | null;
  communityComment?: {
    id: string;
    content?: string | null;
    post?: { id: string; title: string; slug: string } | null;
  } | null;
  community?: { slug: string } | null;
}

type ScanTypeFilter = 'all' | 'ai_detection' | 'nsfw' | 'text_moderation';

interface ContentReviewDialogProps {
  userId: string;
  displayName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function contentTypeLabel(t: TFunction<'admin'>, contentType: string): string {
  if (contentType === 'community_post') return t('moderation.content-type.post');
  if (contentType === 'community_comment') return t('moderation.content-type.comment');
  if (contentType === 'image') return t('moderation.content-type.image');
  if (contentType === 'social_image') return t('moderation.content-type.social-image');
  return contentType;
}

function rowTypeLabel(t: TFunction<'admin'>, scan: FlaggedScan): string {
  if (scan.communityComment) return t('moderation.content-type.comment');
  if (scan.communityPost) return t('moderation.content-type.post');
  return contentTypeLabel(t, scan.contentType);
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function firstImageUrl(images?: SocialImagePreview[] | null): string | null {
  const img = images?.[0];
  return img?.thumbnailUrl || img?.optimizedUrl || null;
}

function getParentContentText(scan: FlaggedScan): { title: string | null; detail: string | null } {
  if (scan.communityPost) {
    const detail = scan.communityPost.content ? stripHtml(scan.communityPost.content).slice(0, 160) : null;
    return { title: scan.communityPost.title, detail };
  }
  if (scan.communityComment) {
    const detail = scan.communityComment.content ? stripHtml(scan.communityComment.content).slice(0, 160) : null;
    return { title: scan.communityComment.post?.title ?? null, detail };
  }
  return { title: null, detail: null };
}

function getScanPreview(scan: FlaggedScan): {
  thumbUrl: string | null;
  fullUrl: string | null;
  title: string | null;
  detail: string | null;
} {
  if (scan.image) {
    const thumbUrl = scan.image.thumbnailUrl || scan.image.optimizedUrl || null;
    const fullUrl = scan.image.optimizedUrl || scan.image.thumbnailUrl || null;
    return { thumbUrl, fullUrl, title: scan.image.title ?? null, detail: null };
  }

  if (scan.socialImage) {
    const thumbUrl = scan.socialImage.thumbnailUrl || scan.socialImage.optimizedUrl || null;
    const fullUrl = scan.socialImage.optimizedUrl || scan.socialImage.thumbnailUrl || null;
    const { title, detail } = getParentContentText(scan);
    return { thumbUrl, fullUrl, title, detail };
  }

  if (scan.communityPost) {
    const post = scan.communityPost;
    const images = post.type?.toUpperCase() === 'IMAGE' ? post.socialImages : post.textImages;
    const thumbUrl = firstImageUrl(images);
    const fullUrl = images?.[0]?.optimizedUrl || images?.[0]?.thumbnailUrl || thumbUrl;
    const detail = post.content ? stripHtml(post.content).slice(0, 160) : null;
    return { thumbUrl, fullUrl, title: post.title, detail };
  }

  if (scan.communityComment) {
    const detail = scan.communityComment.content ? stripHtml(scan.communityComment.content).slice(0, 160) : null;
    const title = scan.communityComment.post?.title ?? null;
    return { thumbUrl: null, fullUrl: null, title, detail };
  }

  return { thumbUrl: null, fullUrl: null, title: null, detail: null };
}

function ContentReviewDialog({ userId, displayName, open, onOpenChange }: ContentReviewDialogProps) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [scanTypeFilter, setScanTypeFilter] = useState<ScanTypeFilter>('all');

  const queryScanType = scanTypeFilter === 'all' ? undefined : scanTypeFilter;

  const { data, loading, refetch } = useQuery(FLAGGED_IMAGES_QUERY, {
    variables: { userId, scanType: queryScanType, limit: 100, offset: 0 },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  const [resolveScan, { loading: resolving }] = useMutation(RESOLVE_SCAN_MUTATION);

  const [pendingBulk, setPendingBulk] = useState<'approved' | 'rejected' | null>(null);
  const [bulkResolving, setBulkResolving] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<{ url: string; title?: string | null } | null>(null);

  const scans: FlaggedScan[] = (data?.flaggedImages?.edges ?? []).map((edge: { node: FlaggedScan }) => edge.node);
  const totalCount = data?.flaggedImages?.totalCount ?? scans.length;

  async function fetchAllFlaggedScans(): Promise<FlaggedScan[]> {
    const limit = 100;
    const allScans: FlaggedScan[] = [];
    let offset = 0;

    while (true) {
      const result = await refetch({ userId, scanType: queryScanType, limit, offset });
      const page = (result.data?.flaggedImages?.edges ?? []).map((edge: { node: FlaggedScan }) => edge.node);
      allScans.push(...page);

      const pageTotal = result.data?.flaggedImages?.totalCount ?? allScans.length;
      offset += limit;
      if (page.length === 0 || offset >= pageTotal) {
        break;
      }
    }

    return allScans;
  }

  async function resolveOne(scanId: string, resolution: 'approved' | 'rejected') {
    const note = resolution === 'rejected' ? t('moderation.violates-community-rules') : undefined;
    try {
      await resolveScan({
        variables: { input: { scanId, resolution, ...(note && { note }) } },
      });
    } catch (err: any) {
      toast.error(err?.message || t('moderation.toast-bulk-error', { count: 1 }));
      return;
    }
    await refetch();
  }

  async function resolveAll(resolution: 'approved' | 'rejected') {
    setBulkResolving(true);
    try {
      const note = resolution === 'rejected' ? t('moderation.violates-community-rules') : undefined;
      const scansToResolve = await fetchAllFlaggedScans();
      const results = await Promise.allSettled(
        scansToResolve.map((s) =>
          resolveScan({
            variables: { input: { scanId: s.id, resolution, ...(note && { note }) } },
          })
        )
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        toast.error(t('moderation.toast-bulk-error', { count: failed }));
      } else {
        toast.success(t('moderation.toast-review-success'));
      }
      await refetch();
    } finally {
      setBulkResolving(false);
    }
  }

  const titleName = displayName || userId;

  function handleOpenChange(next: boolean) {
    if (!next) setScanTypeFilter('all');
    onOpenChange(next);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl w-full max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('moderation.review-requests.dialog-title', { name: titleName })}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Select value={scanTypeFilter} onValueChange={(value) => setScanTypeFilter(value as ScanTypeFilter)}>
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('moderation.scan-type.all')}</SelectItem>
                <SelectItem value="nsfw">{t('moderation.scan-type.nsfw')}</SelectItem>
                <SelectItem value="ai_detection">{t('moderation.scan-type.ai-detection')}</SelectItem>
                <SelectItem value="text_moderation">{t('moderation.scan-type.text-moderation')}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-3"
              disabled={resolving || bulkResolving || totalCount === 0}
              onClick={() => setPendingBulk('approved')}
            >
              {t('moderation.review-requests.approve-all', { count: totalCount })}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="text-xs h-7 px-3"
              disabled={resolving || bulkResolving || totalCount === 0}
              onClick={() => setPendingBulk('rejected')}
            >
              {t('moderation.review-requests.remove-all', { count: totalCount })}
            </Button>
          </div>

          <div className="overflow-auto flex-1">
            {loading && scans.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : scans.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                {t('moderation.review-requests.no-flagged-images')}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        {t('moderation.table.type')}
                      </th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('moderation.table.content')}
                      </th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                        {t('moderation.table.reason')}
                      </th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-44 text-right">
                        {t('moderation.table.actions')}
                      </th>
                      <th className="px-3 py-2 w-16" aria-hidden />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {scans.map((scan) => {
                      const preview = getScanPreview(scan);
                      const typeLabel = rowTypeLabel(t, scan);
                      const contentUrl = getModerationContentUrl(scan);
                      const displayTitle = preview.title || typeLabel;
                      const reasonLabel = (getModerationFlagReason(scan) || '—').replace(/_/g, ' ');

                      return (
                        <tr key={scan.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 align-top">
                            <span className="text-sm font-medium text-gray-900">{typeLabel}</span>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex items-start gap-1.5 min-w-0">
                              <div className="min-w-0 flex-1">
                                {preview.title && (
                                  <div className="text-sm font-medium text-gray-900 line-clamp-2">{preview.title}</div>
                                )}
                                {preview.detail && (
                                  <p className={`text-xs text-gray-600 line-clamp-3 ${preview.title ? 'mt-0.5' : ''}`}>
                                    {preview.detail}
                                  </p>
                                )}
                                {!preview.title && !preview.detail && (
                                  <span className="text-sm text-gray-500">{typeLabel}</span>
                                )}
                              </div>
                              {contentUrl && (
                                <a
                                  href={contentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
                                  title={t('moderation.open-in-new-tab')}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <span className="text-sm text-gray-900">{reasonLabel}</span>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                                disabled={resolving || bulkResolving}
                                onClick={() => resolveOne(scan.id, 'approved')}
                              >
                                {t('moderation.review-requests.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs h-7 px-2"
                                disabled={resolving || bulkResolving}
                                onClick={() => resolveOne(scan.id, 'rejected')}
                              >
                                {t('moderation.review-requests.remove')}
                              </Button>
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top text-right">
                            {preview.thumbUrl ? (
                              <button
                                type="button"
                                className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center overflow-hidden cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ml-auto"
                                onClick={() =>
                                  setLightboxUrl({ url: preview.fullUrl ?? preview.thumbUrl!, title: displayTitle })
                                }
                                title={t('moderation.review-requests.view-full-size')}
                              >
                                <img
                                  src={preview.thumbUrl}
                                  alt={displayTitle}
                                  className="max-w-full max-h-full object-contain"
                                />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {tc('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightboxUrl && (
        <Dialog open onOpenChange={(o) => !o && setLightboxUrl(null)}>
          <DialogContent className="max-w-5xl w-full p-2 flex flex-col items-center bg-black/90 border-0">
            <button
              type="button"
              onClick={() => setLightboxUrl(null)}
              className="absolute top-2 right-2 z-50 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label={tc('close')}
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={lightboxUrl.url}
              alt={lightboxUrl.title ?? ''}
              className="max-h-[85vh] max-w-full object-contain rounded"
            />
            {lightboxUrl.title && <div className="mt-2 text-xs text-gray-300 text-center">{lightboxUrl.title}</div>}
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={pendingBulk !== null} onOpenChange={(o) => !o && setPendingBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulk === 'approved'
                ? t('moderation.review-requests.approve-all-confirm.title', { count: totalCount })
                : t('moderation.review-requests.remove-all-confirm.title', { count: totalCount })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulk === 'approved'
                ? t('moderation.review-requests.approve-all-confirm.description', { count: totalCount })
                : t('moderation.review-requests.remove-all-confirm.description', { count: totalCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkResolving}
              onClick={async () => {
                const action = pendingBulk;
                if (!action) return;
                try {
                  await resolveAll(action);
                } finally {
                  setPendingBulk(null);
                }
              }}
            >
              {pendingBulk === 'approved' ? tc('approve') : tc('remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ContentReviewDialog;
